// Module-level singleton that holds the active project filter.
// Mirrors orgStore but adds localStorage persistence and a subscriber list
// so React components re-render via useActiveProject() when it changes.

const STORAGE_KEY = 'activeProjectId';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private-mode / disabled storage — fall back to in-memory only.
    return null;
  }
}

let currentProjectId: string | null = readStored();
const listeners = new Set<() => void>();

// Fired *before* subscribers are notified, so any cached list a component is
// about to re-read has already been invalidated. Services register here so
// projectStore never imports them (that would create an import cycle:
// service -> projectStore -> service).
const changeHooks = new Set<() => void>();

export const projectStore = {
  getProjectId: (): string | null => currentProjectId,

  setProjectId: (id: string | null) => {
    // No-op when unchanged — prevents a redundant render + cache flush on
    // every auth event that re-asserts the same project.
    if (id === currentProjectId) return;

    currentProjectId = id;
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage failures; the in-memory value is authoritative.
    }

    // Flush caches first, then notify — subscribers must not read stale data.
    changeHooks.forEach(h => h());
    listeners.forEach(l => l());
  },

  subscribe: (cb: () => void): (() => void) => {
    listeners.add(cb);
    // Must return void, not Set.delete's boolean — useSyncExternalStore
    // treats the returned function as the cleanup contract.
    return () => {
      listeners.delete(cb);
    };
  },

  /** Register a cache-invalidation hook fired on every project change. */
  onChange: (hook: () => void): (() => void) => {
    changeHooks.add(hook);
    return () => {
      changeHooks.delete(hook);
    };
  },
};
